import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Book } from '../../../../../types/Book.types';
import { PurchaseOrderLine, PurchaseOrderLinePayload } from '../../../../../types/PurchaseOrder.types';
import { computeLineTotal, computeOrderTotals } from '../../../../../services/PurchaseOrdersService';
import { BookTypeahead } from './BookTypeahead';
import { DEFAULT_TAX_RATE, UNIT_OF_MEASURE_OPTIONS } from '../../../../../constants/purchases';
import { formatMoney, parseNumber } from './format';

// Estado local de una línea del formulario. Los numéricos se guardan como texto
// para que el input pueda quedarse vacío mientras se escribe; se convierten en
// lineDraftToPayload. `id` solo existe en edición (línea ya persistida).
export interface LineDraft {
  key: string;
  id?: string;
  bookId: string | null;
  bookQuery: string;
  sku: string;
  description: string;
  supplierReference: string;
  quantityOrdered: string;
  unitOfMeasure: string;
  unitPrice: string;
  discountPct: string;
  taxRate: string;
}

let draftSeq = 0;
const nextKey = () => `line-${Date.now()}-${draftSeq++}`;

export const emptyLineDraft = (): LineDraft => ({
  key: nextKey(),
  bookId: null,
  bookQuery: '',
  sku: '',
  description: '',
  supplierReference: '',
  quantityOrdered: '1',
  unitOfMeasure: 'unidades',
  unitPrice: '',
  discountPct: '0',
  taxRate: String(DEFAULT_TAX_RATE)
});

export const lineDraftFromLine = (line: PurchaseOrderLine): LineDraft => ({
  key: `persisted-${line.id}`,
  id: line.id,
  bookId: line.bookId,
  bookQuery: line.bookTitle,
  sku: line.sku,
  description: line.description,
  supplierReference: line.supplierReference,
  quantityOrdered: String(line.quantityOrdered),
  unitOfMeasure: line.unitOfMeasure,
  unitPrice: String(line.unitPrice),
  discountPct: String(line.discountPct),
  taxRate: String(line.taxRate)
});

export const lineDraftToPayload = (draft: LineDraft): PurchaseOrderLinePayload => ({
  bookId: draft.bookId,
  sku: draft.sku.trim() || undefined,
  description: draft.description.trim(),
  supplierReference: draft.supplierReference.trim() || undefined,
  quantityOrdered: parseNumber(draft.quantityOrdered),
  unitOfMeasure: draft.unitOfMeasure,
  unitPrice: parseNumber(draft.unitPrice),
  discountPct: parseNumber(draft.discountPct),
  taxRate: parseNumber(draft.taxRate)
});

export const lineDraftTotal = (draft: LineDraft): number =>
  computeLineTotal(parseNumber(draft.quantityOrdered), parseNumber(draft.unitPrice), parseNumber(draft.discountPct));

/** Primer error de validación de las líneas, o '' si están bien. */
export const validateLineDrafts = (lines: LineDraft[]): string => {
  if (lines.length === 0) return 'La orden necesita al menos una línea.';
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const n = i + 1;
    if (!l.description.trim()) return `Línea ${n}: la descripción es obligatoria.`;
    if (parseNumber(l.quantityOrdered) <= 0) return `Línea ${n}: la cantidad debe ser mayor que cero.`;
    if (l.unitPrice.trim() === '' || parseNumber(l.unitPrice, -1) < 0) return `Línea ${n}: el precio unitario no puede ser negativo.`;
    const dto = parseNumber(l.discountPct);
    if (dto < 0 || dto > 100) return `Línea ${n}: el descuento debe estar entre 0 y 100.`;
    if (parseNumber(l.taxRate) < 0) return `Línea ${n}: el IVA no puede ser negativo.`;
  }
  return '';
};

interface PurchaseOrderLinesEditorProps {
  lines: LineDraft[];
  books: Book[];
  currency: string;
  onChange: (lines: LineDraft[]) => void;
  disabled?: boolean;
}

export const PurchaseOrderLinesEditor = ({ lines, books, currency, onChange, disabled }: PurchaseOrderLinesEditorProps) => {
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    onChange(lines.map(l => (l.key === key ? { ...l, ...patch } : l)));

  const removeLine = (key: string) => onChange(lines.filter(l => l.key !== key));

  const addLine = () => onChange([...lines, emptyLineDraft()]);

  const totals = computeOrderTotals(
    lines.map(l => ({ lineTotal: lineDraftTotal(l), taxRate: parseNumber(l.taxRate) }))
  );

  return (
    <div className="module-space-y-3">
      <div className="purchases-view__lines-wrapper">
        <table className="purchases-view__lines-table">
          <thead>
            <tr>
              <th>Libro</th>
              <th>Descripción *</th>
              <th>SKU</th>
              <th>Ref. acreedor</th>
              <th className="right">Cantidad *</th>
              <th>Unidad</th>
              <th className="right">Precio *</th>
              <th className="right">Dto %</th>
              <th className="right">IVA %</th>
              <th className="right">Total</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map(line => (
              <tr key={line.key}>
                <td className="purchases-view__line-book">
                  <BookTypeahead
                    books={books}
                    disabled={disabled}
                    value={{ bookId: line.bookId, query: line.bookQuery }}
                    onChange={(value, book) => updateLine(line.key, {
                      bookId: value.bookId,
                      bookQuery: value.query,
                      // Al elegir un libro se rellenan descripción y SKU; se pueden retocar después.
                      ...(book ? { description: book.title, sku: book.sku || line.sku } : {})
                    })}
                  />
                </td>
                <td className="purchases-view__line-description">
                  <input
                    type="text"
                    value={line.description}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { description: e.target.value })}
                    placeholder="Artículo o servicio"
                    className="module-form-input"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={line.sku}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { sku: e.target.value })}
                    className="module-form-input"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={line.supplierReference}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { supplierReference: e.target.value })}
                    className="module-form-input"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={line.quantityOrdered}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { quantityOrdered: e.target.value })}
                    className="module-form-input right"
                  />
                </td>
                <td>
                  <select
                    value={line.unitOfMeasure}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { unitOfMeasure: e.target.value })}
                    className="module-form-select"
                  >
                    {UNIT_OF_MEASURE_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unitPrice}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { unitPrice: e.target.value })}
                    className="module-form-input right"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={line.discountPct}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { discountPct: e.target.value })}
                    className="module-form-input right"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.taxRate}
                    disabled={disabled}
                    onChange={(e) => updateLine(line.key, { taxRate: e.target.value })}
                    className="module-form-input right"
                  />
                </td>
                <td className="right purchases-view__line-total">{formatMoney(lineDraftTotal(line), currency)}</td>
                <td className="right">
                  <button
                    type="button"
                    onClick={() => removeLine(line.key)}
                    disabled={disabled}
                    className="module-action-btn delete"
                    title="Eliminar línea"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr>
                <td colSpan={11} className="purchases-view__lines-empty">Sin líneas. Añada al menos una.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="purchases-view__lines-footer">
        <button type="button" onClick={addLine} disabled={disabled} className="module-form-cancel-btn">
          <Plus size={12} /> Añadir línea
        </button>
        <div className="purchases-view__totals">
          <span>Base imponible <strong>{formatMoney(totals.subtotal, currency)}</strong></span>
          <span>IVA <strong>{formatMoney(totals.taxTotal, currency)}</strong></span>
          <span className="purchases-view__totals-grand">Total <strong>{formatMoney(totals.totalAmount, currency)}</strong></span>
        </div>
      </div>
    </div>
  );
};
